import React, { useState, useEffect } from "react";
import {
  Page,
  Card,
  DataTable,
  Text,
  BlockStack,
  Badge,
  Box,
  Divider,
} from "@shopify/polaris";
import FilterBar from "../../Components/Analytics/FilterBar";
import analyticsApi from "../../Services/analyticsApi";

const Sales = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    include_manual: false,
  });
  const [salesData, setSalesData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await analyticsApi.getRecentSales(filters);
      setSalesData(response.data.data);
    } catch (error) {
      console.error("Error fetching sales data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  if (isLoading) {
    return (
      <Page title="Sales Activity" backAction={{ content: "Back", url: "/admin/analytics" }}>
        <BlockStack gap="400">
          <Card>
            <Box padding="400">
              <Text>Loading sales data...</Text>
            </Box>
          </Card>
        </BlockStack>
      </Page>
    );
  }

  if (!salesData || !salesData.sales || salesData.sales.length === 0) {
    return (
      <Page title="Sales Activity" backAction={{ content: "Back", url: "/admin/analytics" }}>
        <BlockStack gap="400">
          <FilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            isLoading={isLoading}
          />
          <Card>
            <Box padding="400">
              <Text>No sales data available for the selected filters</Text>
            </Box>
          </Card>
        </BlockStack>
      </Page>
    );
  }

  // Prepare sales data for table (using phone instead of email)
  const salesRows = salesData.sales.map((sale) => {
    const productName = sale.product_name_en || sale.product_name_ar || "N/A";
    const customerName = sale.customer_name || "N/A";
    const customerPhone = sale.customer_phone || "N/A";
    const price = `${sale.price.toFixed(2)} KWD`;
    const date = new Date(sale.created_at).toLocaleString();

    // Gateway badge
    const gatewayBadge = (
      <Badge
        tone={
          sale.verified_from === "MyFatoorah"
            ? "success"
            : sale.verified_from === "RevenueCat"
            ? "info"
            : "warning"
        }
      >
        {sale.verified_from}
      </Badge>
    );

    // Payment method badge
    const paymentMethodBadge = sale.payment_method ? (
      <Badge tone="attention">{sale.payment_method}</Badge>
    ) : (
      <Text variant="bodySm" tone="subdued">
        N/A
      </Text>
    );

    // Product type badge
    const productTypeBadge = (
      <Badge
        tone={
          sale.product_type === "Course"
            ? "success"
            : sale.product_type === "Lecture"
            ? "info"
            : "magic"
        }
      >
        {sale.product_type}
      </Badge>
    );

    return [
      date,
      customerName,
      customerPhone,
      productTypeBadge,
      productName,
      price,
      paymentMethodBadge,
      gatewayBadge,
    ];
  });

  // Calculate summary stats
  const totalSales = salesData.sales.length;
  const totalRevenue = salesData.sales.reduce((sum, sale) => sum + sale.price, 0).toFixed(2);
  const avgOrderValue = (totalRevenue / totalSales).toFixed(2);

  // Count by gateway
  const gatewayCount = salesData.sales.reduce((acc, sale) => {
    acc[sale.verified_from] = (acc[sale.verified_from] || 0) + 1;
    return acc;
  }, {});

  // Count by product type
  const productTypeCount = salesData.sales.reduce((acc, sale) => {
    acc[sale.product_type] = (acc[sale.product_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <Page
      title="Sales Activity"
      subtitle={`All sales transactions${filters.start_date && filters.end_date ? " for selected date range" : ""}`}
      backAction={{ content: "Back", url: "/admin/analytics" }}
    >
      <BlockStack gap="500">
        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          isLoading={isLoading}
        />

        {/* Summary Stats */}
        <Card>
          <Box padding="400">
            <BlockStack gap="300">
              <Text variant="headingLg" as="h2">
                Summary
              </Text>
              <Divider />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "16px",
                }}
              >
                <Box padding="200">
                  <BlockStack gap="100">
                    <Text variant="bodyMd" tone="subdued">
                      Total Sales
                    </Text>
                    <Text variant="headingXl" as="h3">
                      {totalSales}
                    </Text>
                  </BlockStack>
                </Box>
                <Box padding="200">
                  <BlockStack gap="100">
                    <Text variant="bodyMd" tone="subdued">
                      Total Revenue
                    </Text>
                    <Text variant="headingXl" as="h3">
                      {totalRevenue} KWD
                    </Text>
                  </BlockStack>
                </Box>
                <Box padding="200">
                  <BlockStack gap="100">
                    <Text variant="bodyMd" tone="subdued">
                      Average Order Value
                    </Text>
                    <Text variant="headingXl" as="h3">
                      {avgOrderValue} KWD
                    </Text>
                  </BlockStack>
                </Box>
              </div>

              <Divider />

              {/* Gateway Breakdown */}
              <Box>
                <Text variant="headingMd" as="h3" fontWeight="semibold">
                  By Gateway
                </Text>
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginTop: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {Object.entries(gatewayCount).map(([gateway, count]) => (
                    <div
                      key={gateway}
                      style={{
                        padding: "8px 12px",
                        backgroundColor: "#f6f6f7",
                        borderRadius: "4px",
                      }}
                    >
                      <Text variant="bodySm" as="span">
                        {gateway}: <strong>{count}</strong>
                      </Text>
                    </div>
                  ))}
                </div>
              </Box>

              {/* Product Type Breakdown */}
              <Box>
                <Text variant="headingMd" as="h3" fontWeight="semibold">
                  By Product Type
                </Text>
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginTop: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {Object.entries(productTypeCount).map(([type, count]) => (
                    <div
                      key={type}
                      style={{
                        padding: "8px 12px",
                        backgroundColor: "#f6f6f7",
                        borderRadius: "4px",
                      }}
                    >
                      <Text variant="bodySm" as="span">
                        {type}: <strong>{count}</strong>
                      </Text>
                    </div>
                  ))}
                </div>
              </Box>
            </BlockStack>
          </Box>
        </Card>

        {/* Sales Table */}
        <Card>
          <Box padding="400">
            <BlockStack gap="400">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text variant="headingLg" as="h2">
                  All Transactions
                </Text>
                <Text variant="bodyMd" tone="subdued">
                  Showing {totalSales} sales
                </Text>
              </div>
              <Divider />
              <DataTable
                columnContentTypes={[
                  "text",
                  "text",
                  "text",
                  "text",
                  "text",
                  "numeric",
                  "text",
                  "text",
                ]}
                headings={[
                  "Date",
                  "Customer",
                  "Phone",
                  "Type",
                  "Product",
                  "Price",
                  "Payment Method",
                  "Gateway",
                ]}
                rows={salesRows}
              />
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>
    </Page>
  );
};

export default Sales;
